// /admin/tenants/:id/bots/:botId — operator bot configuration.
// The bot's six dimensions as tabs, each composing the design-system editors
// around the BotConfig shape. A ConfigSaveBar tracks unsaved changes. Data via
// the getBotConfig/saveBotConfig seams (mock until the endpoints land).

import { useQuery } from '@tanstack/react-query'
import { greetingReplyForTono } from '@vysto/shared/bot-config/identity'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  type BotConfig,
  getBotConfig,
  operatorKbApi,
  saveBotConfig,
  saveNoMatch,
} from './bot-config-data.js'
import { operatorSimulatorApi } from './flows-data.js'
import { KbPanel } from './KbPanel.js'
import { SimulatorPanel } from './SimulatorPanel.js'
import {
  Button,
  ConfigBlock,
  ConfigCard,
  ConfigSaveBar,
  CostBreakdownSidebar,
  Input,
  ToggleSwitch,
  ModelPickerCards,
  OutOfHoursMessageEditor,
  PageHeader,
  PersonalityToneRadios,
  PhraseListEditor,
  PipelineDiagram,
  ProactiveWindowPicker,
  SandboxToggle,
  Select,
  SensitivityRuleRow,
  SkillCardListItem,
  Spinner,
  Tabs,
  TabPanel,
  TemperatureSlider,
  TonePreview,
  WeeklyScheduleEditor,
} from '../../design-system/index.js'

const TABS = [
  { id: 'resumen', label: 'Resumen' },
  { id: 'personalidad', label: 'Personalidad' },
  { id: 'conocimiento', label: 'Conocimiento' },
  { id: 'reglas', label: 'Reglas' },
  { id: 'horarios', label: 'Horarios' },
  { id: 'avanzado', label: 'Avanzado' },
  { id: 'habilidades', label: 'Flujos' },
  // B1: dress-rehearse the client's bot from the console — the same sinked
  // real-pipeline chat the owner gets in the editor, via the /admin twins.
  { id: 'probar', label: 'Probar' },
]

export function BotConfigPage(): JSX.Element {
  const { id = '', botId = '' } = useParams()
  const navigate = useNavigate()
  const [tab, setTab] = useState('resumen')
  const [cfg, setCfg] = useState<BotConfig | null>(null)
  const [baseline, setBaseline] = useState<string>('')
  const [saving, setSaving] = useState(false)
  // The no-match behaviour saves on its own (live PATCH .../no-match), decoupled
  // from the page's draft cfg/baseline — it's the first real console write.
  const [noMatch, setNoMatch] = useState<BotConfig['onNoMatch']>('handoff')
  const [noMatchSaved, setNoMatchSaved] = useState(false)

  const { data } = useQuery({
    queryKey: ['admin', 'botconfig', id, botId],
    queryFn: () => getBotConfig(id, botId),
  })

  useEffect(() => {
    if (data) {
      setCfg(data)
      setBaseline(JSON.stringify(data))
      setNoMatch(data.onNoMatch)
    }
  }, [data])

  if (!cfg) {
    return (
      <div className="mx-auto w-full max-w-5xl py-10">
        <Spinner label="Cargando configuración…" />
      </div>
    )
  }

  const dirty = JSON.stringify(cfg) !== baseline
  const update = (patch: Partial<BotConfig>): void => setCfg((c) => (c ? { ...c, ...patch } : c))

  async function onSave(): Promise<void> {
    if (!cfg) return
    setSaving(true)
    try {
      await saveBotConfig(id, botId, cfg)
      setBaseline(JSON.stringify(cfg))
    } finally {
      setSaving(false)
    }
  }

  async function onNoMatchChange(value: BotConfig['onNoMatch']): Promise<void> {
    setNoMatch(value)
    setNoMatchSaved(false)
    await saveNoMatch(id, botId, value)
    setNoMatchSaved(true)
  }

  return (
    <div data-testid="admin-bot-config" className="mx-auto w-full max-w-5xl pb-24">
      <PageHeader
        breadcrumb={[
          { label: 'Clientes', href: '/admin/tenants' },
          { label: 'Cliente', href: `/admin/tenants/${id}` },
        ]}
        title={cfg.botName}
        subtitle={`intent: ${cfg.intent}`}
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void navigate(`/admin/tenants/${id}`)}
          >
            ← Volver al cliente
          </Button>
        }
      />

      <div
        role="note"
        className="mt-3 rounded-xl border border-line bg-surface-2 px-3.5 py-2.5 text-[12px] text-text-2"
      >
        Por ahora se guardan: <strong className="font-semibold text-text">Personalidad</strong>{' '}
        (tono y frases prohibidas), <strong className="font-semibold text-text">Avanzado</strong>{' '}
        (modelo, temperatura, sandbox), el comportamiento{' '}
        <strong className="font-semibold text-text">sin coincidencia</strong> (en Conocimiento), las{' '}
        <strong className="font-semibold text-text">frases que disparan handoff</strong> y la{' '}
        <strong className="font-semibold text-text">recuperación de handoff</strong> (en Reglas) y,
        en <strong className="font-semibold text-text">Horarios</strong>, el horario de atención, el
        mensaje fuera de horario y los días de mensajería proactiva. También puedes{' '}
        <strong className="font-semibold text-text">cargar fuentes de conocimiento</strong> (en
        Conocimiento). Para editar un flujo, usa la pestaña{' '}
        <strong className="font-semibold text-text">Flujos</strong> del agente.
      </div>

      <div className="mt-4">
        <Tabs
          tabs={TABS}
          activeId={tab}
          onChange={setTab}
          idBase="bot-config"
          aria-label="Dimensiones del agente"
        />
      </div>

      <div className="mt-5">
        <TabPanel idBase="bot-config" tabId={tab}>
          {tab === 'resumen' && (
            <ConfigCard title="Resumen" subtitle="El camino de cada mensaje">
              <ConfigBlock>
                <PipelineDiagram />
              </ConfigBlock>
            </ConfigCard>
          )}

          {tab === 'personalidad' && (
            <ConfigCard title="Personalidad" subtitle="Tono y frases prohibidas">
              <ConfigBlock label="Tono del agente">
                {/* Formality has ONE control: the client's tú/usted answer in
                    the Perfil del negocio. When the bot has a profile the tono
                    is DERIVED from it, so this radio reports rather than sets. */}
                <PersonalityToneRadios
                  value={cfg.tone}
                  onChange={(tone) => update({ tone })}
                  readOnly={cfg.toneSource === 'perfil'}
                />
              </ConfigBlock>
              <ConfigBlock label="Frases prohibidas">
                <PhraseListEditor
                  phrases={cfg.neverSay}
                  aria-label="Frases prohibidas"
                  onAdd={(p) => update({ neverSay: [...cfg.neverSay, p] })}
                  onRemove={(i) => update({ neverSay: cfg.neverSay.filter((_, x) => x !== i) })}
                />
              </ConfigBlock>
              <ConfigBlock label="Vista previa">
                {/* The line the RUNTIME actually sends when someone opens with
                    "Hola" and the knowledge base has nothing to match — the same
                    shared constant `process-inbound` speaks, derived from the
                    merged tono. Not an illustration of a tone: the tone. */}
                <TonePreview
                  message={greetingReplyForTono(cfg.tone === 'formal' ? 'formal' : 'cordial')}
                />
                <p className="mt-1.5 text-[12px] text-text-2">
                  Así saluda el agente cuando alguien escribe «Hola» y no hay nada que responder
                  desde el conocimiento.
                </p>
              </ConfigBlock>
            </ConfigCard>
          )}

          {tab === 'conocimiento' && (
            <ConfigCard title="Conocimiento" subtitle="Fuentes y recuperación">
              <ConfigBlock label="Cuando no encuentra información">
                <Select
                  aria-label="Comportamiento sin coincidencia"
                  value={noMatch}
                  onChange={(e) => void onNoMatchChange(e.target.value as BotConfig['onNoMatch'])}
                  options={[
                    { value: 'handoff', label: 'Pasar a una persona' },
                    {
                      value: 'generic_fallback',
                      label: 'Que el agente lo diga y siga la conversación',
                    },
                  ]}
                />
                <p className="mt-1.5 text-[12px] text-text-2">
                  {noMatch === 'handoff'
                    ? 'Si el agente no encuentra la respuesta en el conocimiento, envía la frase de handoff y pasa la conversación a una persona.'
                    : 'Si el agente no encuentra la respuesta, lo dice con honestidad y sigue la conversación, sin pasarla a una persona.'}
                  {noMatchSaved ? ' · Guardado' : ''}
                </p>
              </ConfigBlock>
              <ConfigBlock label="Fuentes de conocimiento">
                <KbPanel scopeKey={`op:${id}:${botId}`} api={operatorKbApi(id, botId)} />
              </ConfigBlock>
            </ConfigCard>
          )}

          {tab === 'reglas' && (
            <ConfigCard title="Reglas" subtitle="Sensibilidad y prioridad">
              <ConfigBlock>
                {cfg.rules.map((rule) => (
                  <SensitivityRuleRow
                    key={rule.id}
                    title={rule.title}
                    detail={rule.detail}
                    locked={rule.locked}
                    enabled={rule.enabled}
                    onToggle={(enabled) =>
                      update({
                        rules: cfg.rules.map((r) => (r.id === rule.id ? { ...r, enabled } : r)),
                      })
                    }
                  />
                ))}
              </ConfigBlock>
              <ConfigBlock label="Frases que disparan handoff">
                <PhraseListEditor
                  phrases={cfg.handoffPhrases}
                  tone="sens"
                  aria-label="Frases que disparan handoff"
                  onAdd={(p) => update({ handoffPhrases: [...cfg.handoffPhrases, p] })}
                  onRemove={(i) =>
                    update({ handoffPhrases: cfg.handoffPhrases.filter((_, x) => x !== i) })
                  }
                />
                <p className="mt-1.5 text-[12px] text-text-2">
                  Si el mensaje contiene alguna de estas frases, la conversación pasa a una persona
                  antes de que el agente la procese. Vienen con una lista base para pedir hablar con
                  alguien; puedes editarla y agregar las tuyas.
                </p>
              </ConfigBlock>
              <ConfigBlock label="Cuando una conversación pasa a una persona">
                <Select
                  aria-label="Recuperación de handoff"
                  value={cfg.handoffRecovery.mode}
                  onChange={(e) =>
                    update({
                      handoffRecovery: {
                        ...cfg.handoffRecovery,
                        mode: e.target.value as 'auto_return' | 'sticky',
                      },
                    })
                  }
                  options={[
                    { value: 'auto_return', label: 'Regresar al agente si nadie responde' },
                    {
                      value: 'sticky',
                      label: 'Quedarse con la persona hasta regresarla manualmente',
                    },
                  ]}
                />
                {cfg.handoffRecovery.mode === 'auto_return' ? (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-[12px] text-text-2">Regresar al agente después de</span>
                    <Input
                      type="number"
                      min={1}
                      aria-label="Minutos para regresar al agente"
                      value={String(cfg.handoffRecovery.autoReturnAfterMinutes)}
                      onChange={(e) =>
                        update({
                          handoffRecovery: {
                            ...cfg.handoffRecovery,
                            autoReturnAfterMinutes: Math.max(1, Number(e.target.value) || 1),
                          },
                        })
                      }
                      className="max-w-[90px]"
                    />
                    <span className="text-[12px] text-text-2">minutos sin respuesta humana.</span>
                  </div>
                ) : (
                  <p className="mt-1.5 text-[12px] text-text-2">
                    El agente no vuelve a contestar hasta que regreses la conversación desde la
                    bandeja.
                  </p>
                )}
              </ConfigBlock>
            </ConfigCard>
          )}

          {tab === 'horarios' && (
            <ConfigCard title="Horarios" subtitle="Atención y proactividad">
              <ConfigBlock label="Horario de atención">
                <WeeklyScheduleEditor week={cfg.week} onChange={(week) => update({ week })} />
                <p className="mt-1.5 text-[12px] text-text-2">
                  Es el mismo horario que el cliente ve en los Ajustes de su agente: se guarda en un
                  solo lugar, así que lo que cambies aquí lo verá ahí.
                </p>
              </ConfigBlock>
              <ConfigBlock>
                <OutOfHoursMessageEditor
                  value={cfg.outOfHoursMessage}
                  onChange={(outOfHoursMessage) => update({ outOfHoursMessage })}
                />
              </ConfigBlock>
              <ConfigBlock label="Mensajería proactiva">
                <ProactiveWindowPicker
                  daysOfWeek={cfg.proactiveDays}
                  onChange={(proactiveDays) => update({ proactiveDays })}
                />
              </ConfigBlock>
            </ConfigCard>
          )}

          {tab === 'avanzado' && (
            <ConfigCard title="Avanzado" subtitle="Modelo, temperatura, sandbox">
              <div className="grid gap-4 md:grid-cols-[1fr,240px]">
                <div className="flex flex-col gap-4">
                  <ConfigBlock label="Modelo de respuesta">
                    <ModelPickerCards
                      value={cfg.model}
                      onChange={(model) => update({ model })}
                      models={[
                        {
                          value: 'sonnet',
                          name: 'Sonnet 4.6',
                          description: 'Respuestas complejas, mejor razonamiento.',
                          cost: '$3 / $15 por 1M tokens',
                        },
                        {
                          value: 'haiku',
                          name: 'Haiku 4.5',
                          description: 'Más rápido y económico.',
                          cost: '$1 / $5 por 1M tokens',
                        },
                      ]}
                    />
                  </ConfigBlock>
                  <ConfigBlock label="Temperatura">
                    <TemperatureSlider
                      value={cfg.temperature}
                      onChange={(temperature) => update({ temperature })}
                    />
                  </ConfigBlock>
                  <ConfigBlock>
                    <SandboxToggle
                      checked={cfg.sandbox}
                      onChange={(sandbox) => update({ sandbox })}
                    />
                  </ConfigBlock>
                  <ConfigBlock label="Habilidades (flujos)">
                    <ToggleSwitch
                      checked={cfg.habilidadesEnabled}
                      onChange={(habilidadesEnabled) => update({ habilidadesEnabled })}
                      label="Ejecutar los flujos de este agente"
                    />
                    <p className="mt-1.5 text-[12px] text-text-2">
                      {cfg.habilidadesEnabled
                        ? 'Este agente puede entrar a sus flujos (agendar, cotizar…).'
                        : 'Apagar los flujos de este agente: responde solo con IA y conocimiento.'}
                    </p>
                    <p className="mt-1 text-[12px] text-text-3">
                      Aplica solo a este agente. Los demás agentes no cambian.
                    </p>
                  </ConfigBlock>
                  <ConfigBlock label="Tope de costo por habilidad (USD)">
                    <Input
                      id="skill-cost-ceiling"
                      value={String(cfg.skillCostCeilingUsd)}
                      onChange={(e) => {
                        const v = Number(e.target.value.replace(/[^0-9.]/g, ''))
                        if (!Number.isNaN(v)) update({ skillCostCeilingUsd: v })
                      }}
                      inputMode="decimal"
                      className="max-w-[140px]"
                    />
                    <p className="mt-1.5 text-[12px] text-text-2">
                      Si una sola ejecución de flujo gasta más que esto en LLM, el agente corta y
                      pasa la conversación a una persona.
                    </p>
                  </ConfigBlock>
                </div>
                <CostBreakdownSidebar
                  perConversationUsd={0.018}
                  cacheSavingPct={85}
                  lines={[
                    { label: 'Clasificación (Haiku)', value: '$0.002' },
                    { label: 'Generación (Sonnet)', value: '$0.016' },
                  ]}
                />
              </div>
            </ConfigCard>
          )}

          {tab === 'probar' && (
            <ConfigCard
              title="Probar el agente"
              subtitle="Chatea como si fueras el paciente; nada llega a WhatsApp"
            >
              <ConfigBlock>
                <SimulatorPanel api={operatorSimulatorApi(id, botId)} />
              </ConfigBlock>
            </ConfigCard>
          )}

          {tab === 'habilidades' && (
            <ConfigCard title="Flujos" subtitle="Las conversaciones que el agente sabe llevar">
              <ConfigBlock>
                <div className="flex flex-col gap-2">
                  {cfg.skills.map((skill) => (
                    <SkillCardListItem
                      key={skill.id}
                      name={skill.name}
                      intent={skill.intent}
                      status={skill.status}
                      steps={skill.steps}
                      {...(skill.meta !== undefined ? { meta: skill.meta } : {})}
                      onClick={() => void navigate(`/admin/tenants/${id}/bots/${botId}/flows`)}
                    />
                  ))}
                  {cfg.skills.length === 0 ? (
                    <p className="text-[12.5px] text-text-2">Este agente aún no tiene flujos.</p>
                  ) : null}
                </div>
              </ConfigBlock>
              <ConfigBlock label="Editor de flujo">
                <div className="flex flex-col items-start gap-3 rounded-xl border border-line bg-surface p-4">
                  <p className="text-[12.5px] text-text-2">
                    Cada flujo (sus pasos y ramas) se edita en el editor de conversación del agente.
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => void navigate(`/admin/tenants/${id}/bots/${botId}/flows`)}
                  >
                    Abrir editor de conversación
                  </Button>
                </div>
              </ConfigBlock>
            </ConfigCard>
          )}
        </TabPanel>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-10 mx-auto max-w-5xl px-6">
        <ConfigSaveBar
          dirty={dirty}
          saving={saving}
          saveLabel="Guardar cambios"
          onSave={() => void onSave()}
          onCancel={() => {
            if (data) setCfg(JSON.parse(baseline) as BotConfig)
          }}
        />
      </div>
    </div>
  )
}
